import * as React from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import type { GridColDef, GridCellParams, GridCellEditCommitParams } from '@material-ui/data-grid';
import {
  DataGrid,
} from '@material-ui/data-grid';
import {
  IconButton, Paper, Typography,
  Box, Tooltip, Grid, Button,
} from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import BrokenImageOutlinedIcon from '@material-ui/icons/BrokenImageOutlined';
import CheckCircleOutlinedIcon from '@material-ui/icons/CheckCircle';
import AddIcon from '@material-ui/icons/Add';
import ClearIcon from '@material-ui/icons/Clear';
import ErrorIcon from '@material-ui/icons/Error';
import _ from 'lodash';
import type IBox from '../receiving/ReceivingInterfaces';
import BoxActionMenu from './BoxActionMenu';
import TableCellChip from '../TableCellChip';

interface BoxesScannedProps {
  completedShipments: Array<IBox>;
  deleteBoxHandler: (boxId: string) => void;
  markBoxDamagedHandler: (boxId: string) => void;
  addBoxFunction: (trackingNumber: string, override?: boolean) => Promise<{ isDuplicate: boolean, Id: string }>;
}

const BoxesScanned: React.FC<BoxesScannedProps> = ({
  completedShipments, deleteBoxHandler, markBoxDamagedHandler, addBoxFunction,
}) => {
  const useStyles = makeStyles((theme) => createStyles({
    root: {
      flexGrow: 1,
    },
    boxesScanned: {
      margin: 7,
    },
    tableContainer: {
      boxShadow: 'none',
      border: 'none',
    },
    saveBoxButton: {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
    },
  }));
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [boxId, setBoxId] = React.useState<string | null>(null);
  const [manuallyAddedBoxes, setManuallyAddedBoxes] = React.useState<
  {
    id: number,
    carrier: string | null
    company: string | null
    isDamaged: boolean
    isRush: boolean
    store: string | null
    trackingNumber: string
  }[]
  >([]);

  const addNewManualAddRow = (rowIndex: number) => {
    setManuallyAddedBoxes(
      [...manuallyAddedBoxes, {
        trackingNumber: '', id: rowIndex, isDamaged: false, isRush: false, store: null, company: null, carrier: null,
      }],
    );
  };

  const handleClose = () => {
    setBoxId(null);
    setAnchorEl(null);
  };

  const handleManualAddClick = () => {
    addNewManualAddRow(0);
  };

  const markBoxDamaged = (boxIdParameter: string) => {
    markBoxDamagedHandler(boxIdParameter);
    handleClose();
  };

  const deleteBox = (boxIdParameter: string) => {
    deleteBoxHandler(boxIdParameter);
    handleClose();
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    if (!anchorEl) {
      setBoxId(id);
      setAnchorEl(event.currentTarget);
    }
  };

  const displayVertIcon = (boxIdParameter: string) => (
    <IconButton
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      id="table-kabob"
      onClick={(e) => handleClick(e, boxIdParameter)}
    >
      <MoreVertIcon />
    </IconButton>
  );

  const displayBoxStatus = (boxStatus: string) => {
    if (boxStatus === 'Good') {
      return (
        <TableCellChip icon={<CheckCircleOutlinedIcon />} label={boxStatus} backgroundColor="#DAEADC" fontColor="#6CAE75" />
      );
    }
    return (
      <TableCellChip icon={<BrokenImageOutlinedIcon />} label={boxStatus} backgroundColor="#FDECCC" fontColor="#FAB635" />
    );
  };

  const displayBoxTracking = (boxTracking: string, isHighPriority: boolean) => (
    <Box flex key={`${boxTracking}`}>
      {boxTracking}
      {isHighPriority && (
        <Box clone mx={0.5} pb={0.25}>
          <ErrorIcon color="error" style={{ display: 'inline-block', verticalAlign: 'middle', fontSize: 24 }} />
        </Box>
      )}
    </Box>
  );

  const columns: GridColDef[] = [
    {
      field: 'trackingNumber',
      type: 'string',
      headerName: 'Tracking Number',
      flex: 1,
      renderCell: (item) => (
        ((typeof (item.value) === 'string')
          ? (
            displayBoxTracking(item.value, item.row.boxPriority)
          )
          : <></>
        )
      ),
    },
    {
      field: 'boxStatus',
      type: 'string',
      headerName: 'Box Status',
      flex: 1,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayBoxStatus(value)
          : <></>
        )
      ),
    },
    {
      field: 'id',
      type: 'string',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];

  const rows = completedShipments.map((s) => ({
    id: s.id,
    trackingNumber: s.trackingNumber,
    boxStatus: s.isDamaged ? 'Damaged' : 'Good',
    boxPriority: s.isRush ?? false,
  }));

  const manualAddRows = manuallyAddedBoxes.map((mab, index: number) => ({
    trackingNumber: mab.trackingNumber,
    id: index,
  }));

  const handleClearIconClick = (index: number) => {
    const repairsToAdd = [...manuallyAddedBoxes];
    repairsToAdd.splice(index, 1);
    setManuallyAddedBoxes(repairsToAdd);
  };

  const manualAddColumns: GridColDef[] = [
    {
      field: 'trackingNumber',
      headerName: 'Tracking Number',
      flex: 3,
      editable: true,
      renderCell: ({ value }) => (
        (value === ''
          ? <Typography variant="body2" color="textSecondary">{'Tracking Number'}</Typography>
          : (
            <Typography variant="body2">
              {value}
            </Typography>
          ))
      ),
    },
    {
      field: 'id',
      flex: 0.3,
      renderCell: (params: GridCellParams) => (
        <ClearIcon onClick={() => { if (typeof params.id === 'number') handleClearIconClick(params.id); }} htmlColor="#587B9F" />
      ),
    },
  ];

  const handleEditCellChangeCommitted = (params: GridCellEditCommitParams) => {
    if (params.value && typeof params.value === 'string'
      && typeof params.id === 'number') {
      if (params.field === 'trackingNumber') {
        manuallyAddedBoxes[params.id][params.field] = params.value;
        addNewManualAddRow(params.id + 1);
      }
    }
  };

  const handleSaveManualAdds = () => {
    const addBoxes = manuallyAddedBoxes.filter((mab) => mab.trackingNumber !== '');
    const repairsToAdd = [...addBoxes];
    const idsToClear: number[] = [];
    // TODO: Investigate this disable and make it unnecessary
    // eslint-disable-next-line no-void
    void new Promise<void>((resolve) => {
      addBoxes.forEach(
        // BJR TODO Rewrite this in a way that doesn't abuse promises
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        (box, index, array) => addBoxFunction(box.trackingNumber)
          .then((addBoxReturn) => {
            if (addBoxReturn.isDuplicate) {
              throw new Error('Duplicate');
            }
            idsToClear.push(box.id);
            if (index === array.length - 1) resolve();
          })
          .catch((error: Error) => {
            if (error.message === 'Duplicate') {
              idsToClear.push(box.id);
              resolve();
            }
            if (index === array.length - 1) resolve();
          }),
      );
    })
      .then(() => new Promise<void>((resolve) => {
        idsToClear
          .sort()
          .reverse()
          .forEach((id, index, array) => {
            repairsToAdd.splice(_.findIndex(repairsToAdd, id), 1);
            if (index === array.length - 1) resolve();
          });
      })
        .then(() => {
          setManuallyAddedBoxes(repairsToAdd);
        }));
  };

  return (
    <Paper
      className={classes.boxesScanned}
    >
      <Box p={2}>
        <Box display="flex">
          <Box flexGrow={1}>
            <Typography>{'Boxes Scanned'}</Typography>
          </Box>
          <Tooltip title="Add boxes manually" placement="top">
            <IconButton
              size="small"
              edge="end"
              id="add-boxes-manually"
              onClick={handleManualAddClick}
              disabled={manuallyAddedBoxes?.length > 0}
            >
              <AddIcon color="primary" />
            </IconButton>
          </Tooltip>
        </Box>
        {manuallyAddedBoxes?.length > 0
          && (
            <Box>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <DataGrid
                    rows={manualAddRows}
                    columns={manualAddColumns}
                    headerHeight={0}
                    autoHeight
                    hideFooter
                    disableColumnMenu
                    onCellEditCommit={handleEditCellChangeCommitted}
                    className={classes.tableContainer}
                  />
                </Grid>
                <Grid item xs={9} alignItems="flex-end" style={{ width: '56.8%', color: '#959595' }}>
                  <Box component="span" fontStyle="italic">
                    <Typography variant="caption">{'Once information is added, save your manually added boxes'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveManualAdds();
                        e.preventDefault();
                      }
                    }}
                    onClick={() => handleSaveManualAdds()}
                    fullWidth
                    variant="contained"
                    color="inherit"
                    className={classes.saveBoxButton}
                  >
                    <Typography
                      variant="subtitle2"
                      color="inherit"
                      style={{
                        marginRight: '0.5em',
                      }}
                    >
                      {'Save Boxes  '}
                    </Typography>
                    <SaveIcon
                      color="inherit"
                      style={{
                        marginBottom: '.1em',
                        fontSize: '1.25em',
                      }}
                    />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight
        hideFooter
        disableColumnMenu
        className="noBorder"
      />
      {
        boxId && anchorEl && (
          <BoxActionMenu
            boxId={boxId}
            anchorEl={anchorEl}
            handleMarkDamaged={markBoxDamaged}
            handleDelete={deleteBox}
            handleClose={handleClose}
          />
        )
      }
    </Paper>
  );
};

export default BoxesScanned;
